import { useEffect, useState } from 'react'
import { MobileContentGems } from '@mc/api'
import styles from 'pages/classes/[slug]/components/sections/SkillsYoullLearnSection/StickyCard/StickyCard.module.scss'

type UseStickyCardsStackProps = {
  mobileContentGems: MobileContentGems[]
  cardRefs: React.MutableRefObject<(HTMLDivElement | null)[]>
  containerRef: React.MutableRefObject<HTMLDivElement | null>
  overlayClass: string
}

type StackResult = {
  isStacked: boolean
  dynamicHeight: number | 'auto'
}

export const INITIAL_FONT_SIZE = 28
const FINAL_FONT_SIZE = 16

export const useStickyCardsStack = ({
  mobileContentGems,
  cardRefs,
  containerRef,
  overlayClass,
}: UseStickyCardsStackProps): StackResult => {
  const [isStacked, setIsStacked] = useState(false)
  const [dynamicHeight, setDynamicHeight] = useState<number | 'auto'>('auto')

  useEffect(() => {
    if (!mobileContentGems?.length || isStacked) return

    const handleScroll = () => {
      cardRefs.current.forEach((cardRef, index) => {
        if (!cardRef) return

        let animationProgress = 0
        let overlap = 0

        const rect = cardRef.getBoundingClientRect()
        const expectedStickyTop = (index + 1) * 60
        const isSticky = rect.top <= expectedStickyTop + 2

        if (index < cardRefs.current.length - 1) {
          const nextCardRef = cardRefs.current[index + 1]
          if (nextCardRef) {
            const nextRect = nextCardRef.getBoundingClientRect()
            const currentBottom = rect.bottom
            const nextTop = nextRect.top
            overlap = Math.max(0, currentBottom - nextTop)
          }
        }

        if (isSticky && index < cardRefs.current.length - 1) {
          const nextCardRef = cardRefs.current[index + 1]
          if (nextCardRef) {
            const nextRect = nextCardRef.getBoundingClientRect()
            const nextExpectedTop = (index + 2) * 60
            const distanceOfNextCard = nextRect.top - nextExpectedTop
            if (distanceOfNextCard <= 15) {
              animationProgress = Math.max(
                0,
                Math.min(1, (15 - distanceOfNextCard) / 15),
              )
            }
          }
        }

        const overlayEl = cardRef.querySelector(
          `.${overlayClass}`,
        ) as HTMLElement | null
        const titleCaptionEl = cardRef.querySelector(
          `.${styles.title}`,
        ) as HTMLElement | null

        if (overlayEl) {
          overlayEl.style.opacity = String(animationProgress * 0.65)
        }

        if (titleCaptionEl && overlap > 5) {
          const cardHeight = rect.height
          const baseDistance = cardHeight - 70
          const directMove = Math.min(overlap, baseDistance)

          titleCaptionEl.style.setProperty(
            'transform',
            `translateY(-${directMove}px)`,
            'important',
          )

          // Calculate font size progress based on scroll distance
          const fontSizeProgress = Math.min(directMove / baseDistance, 1)
          const startFontSize = INITIAL_FONT_SIZE
          const endFontSize = FINAL_FONT_SIZE
          const currentFontSize =
            startFontSize - fontSizeProgress * (startFontSize - endFontSize)

          titleCaptionEl.style.setProperty(
            'font-size',
            `${currentFontSize}px`,
            'important',
          )
        }
      })

      const lastIndex = cardRefs.current.length - 1
      const lastCardRef = cardRefs.current[lastIndex]

      if (lastCardRef) {
        const lastRect = lastCardRef.getBoundingClientRect()
        const lastExpectedTop = (lastIndex + 1) * 60
        const lastIsSticky = lastRect.top <= lastExpectedTop + 2

        if (lastIsSticky) {
          const realCardHeight = lastRect.height || 450
          const calculatedHeight =
            (mobileContentGems.length - 1) * 60 + realCardHeight + 32

          const originalHeight = containerRef.current?.scrollHeight || 0
          const currentScrollY = window.scrollY
          const containerTop =
            containerRef.current?.getBoundingClientRect().top || 0
          const containerOffsetTop = currentScrollY + containerTop

          setIsStacked(true)
          setDynamicHeight(calculatedHeight)

          requestAnimationFrame(() => {
            const newHeight = containerRef.current?.scrollHeight || 0
            const heightDifference = originalHeight - newHeight

            if (currentScrollY > containerOffsetTop && heightDifference > 0) {
              const newScrollY = currentScrollY - heightDifference
              window.scrollTo({
                top: Math.max(0, newScrollY),
                behavior: 'instant',
              })
            }
          })
        }
      }
    }

    handleScroll()

    let ticking = false
    const smoothHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', smoothHandleScroll)

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', smoothHandleScroll)
    }
  }, [mobileContentGems, isStacked, cardRefs, containerRef, overlayClass])

  return { isStacked, dynamicHeight }
}
